import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SimpleTable from "../../../../../components/SimpleTable";
import { useDeleteDialog } from "../../../../../components/shared/Dialog/useDeleteDialog";
import Form from "./Form";
import { useColumns } from "./hooks/useColumns";
import { useRoles } from "./hooks/useRoles";
import { useCompanyPreferences } from "../../hooks/usePreferences";
import { usePreferenceMutation } from "../../api/usePreferenceMutation";

function Rate() {
    const { t } = useTranslation();
    const [mode, setMode] = useState<"create" | "edit">("create");
    const [selected, setSelected] = useState<any>(undefined);
    const [rows, setRows] = useState<any[]>([]);
    const { data: roles, isLoading: rolesLoading } = useRoles();
    const rate = useCompanyPreferences("rate");
    const { mutate, isPending } = usePreferenceMutation("company", "rate");

    useEffect(() => {
        if (Array.isArray(rate)) setRows(rate);
    }, [rate]);

    const goToEdit = (row: any) => {
        setMode("edit");
        setSelected(row);
    };

    const goToCreate = () => {
        setMode("create");
        setSelected(undefined);
    };

    const handleMutate = useCallback((updated: any[], callbacks?: any) => {
        mutate(updated, {
            ...callbacks,
            onSuccess: (...args: any[]) => {
                setRows(updated);
                callbacks?.onSuccess?.(...args);
            },
        });
    }, [mutate]);

    const { open: openDelete, dialog: deleteDialog } = useDeleteDialog({
        message: () => (
            <div className="tw-flex tw-flex-col">
                <h1 className="tw-font-bold tw-text-lg">{t("delete-rate-config")}</h1>
                {t("do-you-want-to-delete-rate-config")}
            </div>
        ),
        loading: isPending,
        onConfirm: (row: any) => {
            const updated = rows.filter(({ role }: any) => role !== row?.role);
            handleMutate(updated);
        },
    });

    const columns = useColumns({ roles, edit: goToEdit, remove: openDelete });

    return (
        <div className="tw-p-5 tw-h-full tw-rounded-lg tw-bg-white">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-border tw-py-2 tw-px-3 tw-mb-4">
                <h1 className="tw-font-bold">{t("rate")}</h1>
            </div>

            <div className="tw-flex tw-items-start tw-gap-6 tw-px-7 tw-flex-wrap">
                <Form
                    key={selected?.role ?? "new"}
                    mode={mode}
                    values={selected}
                    config={rows}
                    mutate={handleMutate}
                    isPending={isPending}
                    roles={roles}
                    close={goToCreate}
                />

                <div className="tw-flex-[2] tw-min-w-0 tw-basis-96">
                    <SimpleTable
                        data={rows}
                        columns={columns}
                        loading={rolesLoading}
                        enableRowSelection={false}
                        footer={false}
                        getRowProps={undefined}
                    />
                </div>
            </div>

            {deleteDialog}
        </div>
    );
}

export default Rate;
